import _ from "lodash";
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  memo,
  useImperativeHandle,
  useMemo,
} from "react";

// Components
import I18nHOC from "UIComponents/I18nHOC";
import MarkdownView from "UIComponents/MarkdownView";
import Loading from "UIComponents/Loading";
import AIFeedbackStrip from "AppComponents/AIFeedbackStrip";
import AIContextBar from "AppComponents/AIContextBar";
import SuggestionLibrary from "./components/SuggestionLibrary";
import PrerequisiteSelection from "./components/PrerequisiteSelection";
import NewTag from "./components/NewTag";
import PromptCards from "./components/PromptCards";

// Design system
import {
  IconButton,
  Avatar,
  Button,
  Tooltip,
  Modal,
  Checkbox,
} from "@toddle-edu/ds-web";
import {
  RefreshArrowOutlined,
  Shifu3D,
  CloseOutlined,
  TickSmallOutlined,
  TickCircleFilled,
} from "@toddle-edu/ds-icons";
import { colors, fontStyles, elevations } from "@toddle-edu/ds-theme";

// Utils / Services / Constants
import {
  hideIntercom,
  showIntercom,
  resetAIConversation,
  updateAIConversation,
  getVariablesForLocalisedPlannerTemplates,
} from "modules/Services";
import { updateSettings } from "modules/ConfigurationThunks";
import {
  checkScrollAtBottom,
  getStyleStrippedText,
  htmlToText,
  onEnterOrSpaceKeyDown,
  scrollIntoView,
} from "Utils";
import { copyToClipboardWithMimeType } from "Utils/CopyToClipboard";
import {
  getBlockActions,
  getCombinedPrompts,
  getIsAIResponseLoading,
  getFormattedAssessmentFields,
  getParamsForAddToLEAction,
  getIsListTitleBold,
  getTextValueForBlock,
  getToolTypeFromContent,
  getSelectedFieldHasValue,
  getIsMYPRubric,
  getMYPObjectivesData,
  getAssessmentFieldValues,
  getHTMLContentForBlock,
  getTutorChunks,
  convertXMLToObj,
  getAIPromptFilters,
  getAITutorTopSixPrompts,
  customContentParser,
} from "./modules/Utils";
import {
  SINGLE_POINT_RUBRIC,
  WRITING,
  LOADING,
  BROWSING_THE_INTERNET,
  DP_ASSESSMENT_RUBRIC,
  STREAMING,
  ASSESSMENT,
  SCORE_BASED_RUBRIC,
  STANDARD_BASED_RUBRIC,
  THINKING,
} from "Constants/stringConstants";
import {
  EMOJI_COLOR_TRAY,
  DIALOGS_INFO,
  INITIAL_STATE,
} from "./modules/Constants";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import { getSettingValue } from "Utils/ConfigurationUtils";
import {
  AI_TRANSLATION_LANGUAGES,
  ASSESSMENT_TOOLS_OBJ,
} from "@toddle-edu/common-utils";
import { TEMP_ID } from "AppComponents/AIContextBar/modules/Constants";
import {
  getCommonMarkdownComponents,
  isSchoolisationEnabled,
} from "Utils/AIUtils";
import { getEmojisAndTextFromString } from "Utils/StringHelpers";
import {
  changeDisciplinaryContentToMultiField,
  getFieldValue,
  getUnitPlanDetails,
} from "Assessments/modules/Utils";
import { getIsLearningCoursesEnabled } from "Teacher/routes/LearningCourses/modules/Utils";

// third party libs.
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";
import withDerivedProps from "lib/withDerivedProps";
import classNames from "classnames";

// Modules
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import {
  getSelectedCurriculumId,
  getCurrentCurriculumProgramType,
  getSelectedAcademicYearId,
  getUserId,
  getUserType,
  getOrganizationId,
} from "modules/CommonSelectors";
import { getLocalisedTemplate } from "IBPlanner/modules/IBPlannerModule";
import {
  updateAssignmentField,
  cancelRequest,
  onSendQuery,
  createAIConversation,
  captureAndUploadImage,
} from "./modules/LessonPlanningAssistantModule";
import { getCurrentAssessmentTypes } from "Platform/modules/PlatformModule";
import { getAssessmentDetailsForAIEnhancer } from "Assessments/modules/AssessmentQueryEnhancers";
import { removeAssessmentTool } from "Assessments/modules/AssessmentModule";
import { createAssessmentTool } from "AssessmentToolSetup/modules/AssessmentToolSetupModule";
import { getPlannerTemplateFromCache } from "modules/CommonGraphqlHelpers";

// GraphQL
import ConditionalWrapper from "Utils/ConditionalWrapper";
import {
  getAIConversationsQuery,
  getAIPromptsQuery,
  getUnitPlanBasicDetailsQuery,
} from "./modules/LessonPlanningQueries";

//Feature flag
import { getIsAITutorEnhancementsV2Enabled } from "Utils/ACLStoreUtils";

// Scss
import classes from "./LessonPlanningAssistant.scss";
import { getSchoolisationInstructionsQuery } from "Platform/routes/AdminSettings/routes/SchoolisationSettings/modules/SchoolisationQueries";

const styles = {
  overlayInnerStyle: {
    maxWidth: "240px",
  },
  containerStyle: {
    padding: "0",
    backgroundColor: colors.white,
  },
  inputContainerStyle: {
    flex: "1",
  },
  inputStyle: {
    outline: "none",
    border: "0px",
    padding: "0px",
    borderRadius: "0px",
    fontSize: "1.4rem",
    lineHeight: "2rem",
    color: colors.textDefault,
    ...fontStyles.medium,
  },
};

const LessonPlanningAssistant = memo(props => {
  const {
    t,
    setToastMsg,
    setShowAssistantRightPane,
    portalType,
    leId,
    messages,
    aiConversationId,
    updateAssignmentField,
    resetAIConversation,
    onSendQuery,
    prompts,
    assessmentType,
    gradeId,
    assessmentDetails,
    isInitialState,
    getCurrentAssessmentTypes,
    course,
    middlePaneRef,
    createAIConversation,
    updateAIConversation,
    mode,
    entityType,
    sourceFrom,
    isFromUnitPlan,
    courseId,
    unitPlanDetails,
    unitPlanTemplate: _unitPlanTemplate,
    assessmentTemplate,
    refetchAssessments,
    updateSettings,
    userId,
    curriculumType,
    captureAndUploadImage,
    selectedCourse,
    creationMode,
    isDraft,
    isAITutor,
    conversationMetadata,
    conversationAttachments,
    showLoader,
    customRef,
    templateId,
    testId,
    shouldSetInitialPrompt,
    learningCourseId,
    context,
    setContext,
    isData,
    isLoading,
    isAITutorEnhancementsV2Enabled,
  } = props;

  const currentAssessmentTypes = getCurrentAssessmentTypes();

  // states

  const [currentAIState, setCurrentAIState] = useState("");
  const [currentDialogAction, setCurrentDialogueAction] = useState(
    INITIAL_STATE.DIALOG_ACTION
  );
  const [selectedAction, setSelectedAction] = useState({});
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
  const [promptSuggestionModalInfo, setPromptSuggestionModalInfo] = useState(
    INITIAL_STATE.PROMPT_SUGGESTION_MODAL_INFO
  );

  const [aiVoiceModalLanguage, setAiVoiceModalLanguage] = useState("en");
  const [excludedFields, setExcludedFields] = useState([]);
  const [isAddButtonDisabled, setIsAddButtonDisabled] = useState(false);

  const [selectedTutorIdeaId, setSelectedTutorIdeaId] = useState(null);
  const [sendButtonParams, setSendButtonParams] = useState({});

  // refs
  const containerRef = useRef(null);
  const scrollEndRef = useRef(null);

  // whenever the user changes the language in the AI voice modal then
  // set the selected language in local state and load that language bundle
  const setConfigureAIVoiceRef = useCallback(async node => {
    const { preferredLanguageOptions: languageOptions, preferredVoiceObj } =
      node || {};

    const selectedLanguage = _.get(preferredVoiceObj, "language", "en");

    const languageKey = _.find(languageOptions, {
      key: selectedLanguage,
    })?.lng;

    if (languageKey) {
      setAiVoiceModalLanguage(languageKey);
    }
  }, []);

  // side effects

  // hide intercom because it is coming above chat send button
  useEffect(() => {
    const { showIntercom, hideIntercom, isAITutor } = props;
    hideIntercom();
    return () => {
      if (!isAITutor) showIntercom();
    };
  }, []);

  // on message sent scroll to bottom
  useEffect(() => {
    if (!_.isEmpty(messages)) {
      scrollToBottom();
    }
  }, [messages, shouldScrollToBottom, currentAIState]);

  // send message on prompt selection
  useEffect(() => {
    if (!_.isEmpty(selectedAction) && _.isEmpty(selectedAction?.label)) {
      onSendMessage();
    }
  }, [selectedAction]);

  // create ai message after createAttachment mutation success
  useEffect(() => {
    if (
      _.get(sendButtonParams, "attachmentHasTempId") &&
      _.every(conversationAttachments, ({ id }) => !_.includes(id, TEMP_ID))
    ) {
      onSendMessage();
    }
  }, [conversationAttachments, sendButtonParams?.attachmentHasTempId]);

  // if user closes the AI chat while the response is still being generated
  // and then re-opens it again we need to set current AI state
  useEffect(() => {
    const lastMessageId = _.get(_.last(messages), "node.id");
    if (_.isEmpty(currentAIState) && _.includes(lastMessageId, "NEW")) {
      setCurrentAIState(STREAMING);
    } else if (
      !_.includes(lastMessageId, "NEW") &&
      currentAIState === STREAMING
    ) {
      setCurrentAIState("");
    }
  }, [messages, currentAIState]);

  // create a new ai conversation for old assignments (where ai conversation does not exist)
  useEffect(() => {
    const shouldCreateConversation =
      leId && _.isEmpty(aiConversationId) && !_.isEmpty(assessmentDetails);

    if (shouldCreateConversation) {
      const _createAIConversation = async () => {
        setCurrentAIState(LOADING);
        await createAIConversation({
          input: {
            entityType: ASSESSMENT,
            entityId: leId,
          },
        });
        setCurrentAIState("");
      };

      _createAIConversation();
    }
  }, [mode, aiConversationId, leId, assessmentDetails, createAIConversation]);

  useImperativeHandle(
    customRef,
    () => ({
      messages,
      isAIResponseLoading: getIsAIResponseLoading(currentAIState),
      setCurrentDialogueAction,
    }),
    [messages, currentAIState]
  );

  const initialValue = useMemo(() => {
    if (!shouldSetInitialPrompt) return null;

    // only set initial value if there has been no prior conversation
    if (!_.isEmpty(messages)) return null;

    const prompt = _.get(assessmentDetails, "metaData.aiPrompt", "");
    return htmlToText(prompt);
  }, [_.get(assessmentDetails, "metaData.aiPrompt", ""), messages]);

  // don't render initial prompts when created through AI
  const isPromptsHidden =
    isInitialState && shouldSetInitialPrompt && !_.isEmpty(initialValue);

  const emptyStateLocales = useMemo(() => {
    if (isPromptsHidden) {
      return {
        assessmentGreetMessage:
          "classRoom:toddle_ai_empty_state_greet_subtext_with_suggestion_library",
        aiTutorTitle: "ai:ai_tutor_empty_state_without_prompts_msg",
      };
    }

    return {
      assessmentGreetMessage:
        "classRoom:toddle_ai_empty_state_greet_subtext_with_label",
      aiTutorTitle: "ai:ai_tutor_empty_state_greet_msg",
    };
  }, [isPromptsHidden]);

  const unitPlanTemplate = _.get(
    getLocalisedTemplate({
      template: { body: _.cloneDeep(_unitPlanTemplate) },
      t,
      templateType: "unitPlan",
    }),
    "body",
    {}
  );

  const fieldList = _.get(assessmentTemplate, "field_list", {});
  const stepList = _.get(assessmentTemplate, "step_list", {});

  const { action: dialogAction, params: dialogParams = {} } =
    currentDialogAction || {};

  const isLearningCoursesEnabled = getIsLearningCoursesEnabled();

  // functions

  const scrollToBottom = () => {
    if (scrollEndRef.current && shouldScrollToBottom) {
      scrollIntoView({ element: scrollEndRef.current });
    }
  };

  const updatePromptSuggestionModalInfo = params => {
    setPromptSuggestionModalInfo(prevState => {
      return {
        ...prevState,
        ...params,
      };
    });
  };

  const onPromptSelection = prompt => {
    setSelectedAction(prompt);
  };

  const onDialogConfirm = async currentAction => {
    const { action, params = {} } = currentAction || currentDialogAction || {};

    switch (action) {
      case "CLEAR_CHAT":
      case "CLEAR_CHAT_AND_GENERATE": {
        // 1. show full screen loader
        setCurrentAIState(LOADING);

        // 2. reset the AI conversation
        await resetAIConversation({
          input: { id: aiConversationId },
        });

        // 3. update the selected language in the AI conversation and reset promptFields
        const selectedLanguage = _.find(AI_TRANSLATION_LANGUAGES, {
          code: aiVoiceModalLanguage,
        });

        if (selectedLanguage) {
          await updateAIConversation({
            input: {
              id: aiConversationId,
              metadata: {
                language: _.get(selectedLanguage, "label"),
                languageCode: aiVoiceModalLanguage,
                selectedDropdownLanguage: _.get(selectedLanguage, "id"),
                promptFields: [],
              },
            },
          });
        }

        // 4. reset states
        setCurrentAIState("");
        setExcludedFields([]);
        setContext({});

        if (action === "CLEAR_CHAT_AND_GENERATE") {
          const { isChecked = false, selectedPrompt } = params || {};

          if (isChecked) {
            updateSettings({
              settings: [
                {
                  name: "ShowResetAIChatDialog",
                  value: "true",
                },
              ],
              showToastMsg: false,
            });
          }

          if (selectedPrompt) setSelectedAction(selectedPrompt);
          updatePromptSuggestionModalInfo({ showModal: false });
        }

        break;
      }
      case "REPLACE_CONTENT": {
        // if "do not show this message again" checkbox is checked then
        // save the user setting so that in future this dialog will not be shown
        if (params?.isChecked) {
          updateSettings({
            settings: [
              {
                name: "ShowReplaceContentDialog",
                value: "true",
              },
            ],
            showToastMsg: false,
          });
        }

        handleActions(params);
        break;
      }

      default: {
        handleActions(params);
      }
    }

    setCurrentDialogueAction({ action: "" });
  };

  // send message on enter

  const onSendMessage = async data => {
    const { checkLoadingState = true, content } = sendButtonParams;
    const { promptFields = [] } = data || {};

    if (getIsAIResponseLoading(currentAIState) && checkLoadingState) return;

    setShouldScrollToBottom(true);
    await onSendQuery({
      t,
      query: content || data?.content,
      leId,
      aiConversationId,
      selectedAction,
      setSelectedAction,
      setCurrentAIState,
      gradeId,
      course,
      messages,
      language: aiVoiceModalLanguage,
      context,
      setContext,
      fieldList,
      unitPlanDetails,
      assessmentDetails,
      excludedFields,
      entityType,
      stepList,
      template: assessmentTemplate,
      promptFields,
      prompts,
      unitPlanTemplate,
      conversationAttachments,
      refetchAssessments,
      isAITutor,
      mode,
      setSendButtonParams,
      sendButtonParams,
      learningCourseId,
      schoolInstructions: props.schoolInstructions,
    });
  };

  const onClickStop = () => {
    cancelRequest?.();
    setCurrentAIState("STOP");
  };

  const onScrollBodyContainer = e => {
    setShouldScrollToBottom(checkScrollAtBottom(e.target, 20)); // ignore paddingBottom of 56px
  };

  const renderPrompts = () => {
    const combinedPrompts = getCombinedPrompts({
      t,
      prompts,
      language: aiVoiceModalLanguage,
      assessmentDetails,
      unitPlanDetails,
      curriculumProgramType: curriculumType,
      sourceFrom: "CHAT",
    });

    const showPrompts = !_.isEmpty(combinedPrompts);

    return showPrompts ? (
      <div className={classes.promptsContainer}>
        {_.map(combinedPrompts, (prompt, index) => {
          const { id, options, label, style, visible = true } = prompt || {};

          if (!visible) return null;

          const { emoji, text } = getEmojisAndTextFromString(label) || {};

          const promptOptions = _.map(options, option => {
            const { id: optionId, label, isNewTagVisible } = option || {};

            const onClickOption = () => {
              if (optionId === "MORE") {
                updatePromptSuggestionModalInfo({
                  showModal: true,
                  params: { id },
                });
              } else {
                setSelectedAction(option);
              }
            };

            const handleKeyDown = e => {
              if (e.key === "Enter" || e.key === " ") {
                onClickOption();
              }
            };

            return (
              <div
                key={`${id}_${optionId}`}
                className={classes.tag}
                style={style}
                onClick={onClickOption}
                onKeyDown={handleKeyDown}
                tabIndex={0}
                role="button"
                aria-label="prompt"
                data-test-id={`${testId}-lessonPlanningAssistant-prompt-card`}
              >
                {label}
                <NewTag t={t} isNewTagVisible={isNewTagVisible} />
              </div>
            );
          });

          const labelClass = classNames(
            "text-label-2",
            classes.textDefault,
            classes.tagContainer
          );

          const groupedTagsClass = classNames(
            classes.flexDirectionColumn,
            classes.fullWidth
          );

          return (
            <div className={groupedTagsClass}>
              <div className={labelClass}>
                {emoji ? (
                  <div
                    className={classes.emojiContainer}
                    style={{
                      backgroundColor:
                        EMOJI_COLOR_TRAY[index % _.size(EMOJI_COLOR_TRAY)],
                    }}
                  >
                    {emoji}
                  </div>
                ) : null}
                <div className={classes.flexDirectionColumn}>
                  <div className={classes.promptGroupLabel}>{text}</div>
                  <div className={classes.flexWrap}>{promptOptions}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    ) : null;
  };

  const renderEmptyState = () => {
    if (isPromptsHidden) return;

    if (isAITutor) {
      const promptsToDisplay = getAITutorTopSixPrompts({
        prompts,
      });

      return (
        <div className={classes.emptyState}>
          <PromptCards
            prompts={promptsToDisplay}
            allPrompts={prompts}
            setSelectedAction={setSelectedAction}
            leId={leId}
            assessmentDetails={assessmentDetails}
            gradeId={gradeId}
            assessmentTemplate={assessmentTemplate}
            updatePromptSuggestionModalInfo={updatePromptSuggestionModalInfo}
            portalType={portalType}
          />
        </div>
      );
    } else {
      return <div className={classes.emptyState}>{renderPrompts()}</div>;
    }
  };

  const handleActions = async params => {
    const { key, content, language, assessmentToolTypes, metadata } = params;

    setIsAddButtonDisabled(true);

    switch (key) {
      case "copy": {
        const textContent = await getTextValueForBlock({ content });
        const htmlContent = getHTMLContentForBlock({ content });

        copyToClipboardWithMimeType(
          {
            text: textContent,
            html: htmlContent,
          },
          () => {
            setToastMsg({
              msg: "toastMsgs:copied_capital",
              type: "success",
            });
          }
        );
        break;
      }
      case "add_assessment_tool": {
        if (!_.isEmpty(assessmentToolTypes)) {
          const promises = [];
          _.forEach(assessmentToolTypes, assessmentToolType => {
            promises.push(
              updateAssignmentField({
                t,
                key,
                assessmentDetails,
                field_list: fieldList,
                portalType,
                content,
                selectedAssessmentField: assessmentToolType,
                metadata,
                refetchAssessments,
                isAITutor,
              })
            );
          });

          await Promise.all(promises);
        } else {
          updateAssignmentField({
            t,
            key,
            assessmentDetails,
            field_list: fieldList,
            portalType,
            content,
            metadata,
            refetchAssessments,
          });
        }

        break;
      }
      case "add_to_le": {
        // get title, description, learning intentions from the AI content by using regex
        const assessmentFieldValues = await getFormattedAssessmentFields({
          inputString: content,
          captureAndUploadImage,
          assessmentToolTypes,
          language,
          isAITutor,
          fieldList,
        });

        // parse the data (trim, remove extra spaces, filter empty data etc.)
        const params = await getParamsForAddToLEAction({
          assessmentFieldValues,
          fieldList,
          assessmentToolTypes,
        });

        // update assessment details
        updateAssignmentField({
          t,
          key,
          assessmentDetails,
          params,
          field_list: fieldList,
          portalType,
          content,
          isAITutor,
        });

        // add DP rubric if sourceDPRubricID is there in the metadata
        updateAssignmentField({
          t,
          key: "add_assessment_tool",
          assessmentDetails,
          params,
          field_list: fieldList,
          portalType,
          content,
          selectedAssessmentField: DP_ASSESSMENT_RUBRIC,
          metadata,
        });

        break;
      }
    }

    setIsAddButtonDisabled(false);
  };

  const renderLoadingText = useCallback(() => {
    if (currentAIState === THINKING) {
      return t("ai:thinking");
    }

    if (currentAIState === WRITING) {
      if (isAITutor && isAITutorEnhancementsV2Enabled) {
        return t("ai:thinking");
      }

      return t("classRoom:writing");
    }

    return t("ai:browsing_the_internet");
  }, [currentAIState, isAITutorEnhancementsV2Enabled, isAITutor, t]);

  const renderMessages = () => {
    const markdownComponents = getCommonMarkdownComponents({
      anchorClassName: classes.anchor,
    });

    const isLoadingState = _.includes(
      [BROWSING_THE_INTERNET, WRITING, THINKING],
      currentAIState
    );

    const lastMessage = _.last(messages);
    const isLastMessageStreaming = _.includes(
      _.get(lastMessage, "node.id", ""),
      "NEW"
    );

    const showLoadingAnimation =
      isLoadingState &&
      !(
        isLastMessageStreaming &&
        !_.isEmpty(
          _.trim(customContentParser(_.get(lastMessage, "node.content", "")))
        )
      );

    return (
      <React.Fragment>
        {_.map(messages, ({ node }, index) => {
          const {
            id,
            authorType,
            content,
            metadata,
            createdBy,
            votes,
            usageMetricsId,
          } = node || {};

          const { profileImage } = createdBy || {};
          const { language } = metadata || {};

          const formattedUserName = getFormattedUserName({
            user: createdBy,
            type: "staff",
          });

          switch (authorType) {
            case "user": {
              return (
                <div key={id} className={classes.messageContainer}>
                  <div className={classes.userMsg}>
                    <Avatar
                      size={"medium"}
                      shape={"square"}
                      shade={"light"}
                      className={classes.noShrink}
                      name={formattedUserName}
                      src={profileImage}
                    />

                    <div
                      className={classes.singleMsg}
                      dangerouslySetInnerHTML={{
                        __html: getStyleStrippedText({
                          text: content,
                          type: "SANITIZE_DANGER_HTML_PARAGRAPH",
                        }),
                      }} // to show links in the content
                    />
                  </div>
                </div>
              );
            }

            case "assistant": {
              let block = null;

              const {
                userInputFlagged,
                fieldType: field,
                model,
                temperature,
                intentUsageMetricsId,
              } = metadata || {};

              const isStandardOrScoreRubric = [
                SCORE_BASED_RUBRIC,
                STANDARD_BASED_RUBRIC,
              ].includes(field);

              const outputMatch = content.match(
                /<output>([\s\S]*?)<\/output>/i
              );
              const contentForToolDetection = outputMatch
                ? outputMatch[1]
                : content;

              const assessmentToolTypes = getToolTypeFromContent({
                language: language || aiVoiceModalLanguage,
                content: contentForToolDetection,
                metadata,
                assessmentDetails,
              });

              let updatedContent = customContentParser(content);

              // strip <output> wrapper tags
              updatedContent = _.replace(updatedContent, /<\/?output>/gi, "");
              if (_.isEmpty(_.trim(updatedContent))) return null;

              const assessmentFieldValues = getAssessmentFieldValues({
                inputString: updatedContent,
                shouldFilterEmptyValues: true,
                assessmentToolTypes,
                language,
                isAITutor,
              });

              const blockContentClass = classNames({
                [classes.blockContentText]: true,
                [classes.tableWithEmptyHeaders]: isAITutor,
                [classes.boldMarker]: getIsListTitleBold(updatedContent),
                [classes.assessmentToolMarkdown]: !_.isEmpty(
                  ASSESSMENT_TOOLS_OBJ[field]
                ),
                [classes.singlePointRubricTable]: field === SINGLE_POINT_RUBRIC,
                [classes.standardRubricTable]: isStandardOrScoreRubric,
              });

              // do not translate AI generated content, as the HTML structure can be anything (which causes problem with google translate)
              // https://github.com/facebook/react/issues/11538#issuecomment-390386520

              const blockClass = classNames({
                ["notranslate"]: true,
                [classes.block]: true,
                [classes.textCritical]: userInputFlagged,
                [classes.dpAssessmentRubric]:
                  _.first(assessmentToolTypes) === DP_ASSESSMENT_RUBRIC,
              });

              const blockWrapperClass = classNames({
                [classes.blockWrapper]: true,
                [classes.violationBlock]: userInputFlagged,
              });

              if (isAITutor) {
                const dividedChunks = getTutorChunks(updatedContent);

                const loaderMessageClass = classNames(
                  classes.generatingSingleMsg,
                  {
                    [classes.loaderMsg]: !isAITutorEnhancementsV2Enabled,
                  }
                );

                block = _.map(dividedChunks, chunk => {
                  const { text, type, xml, isLocalised = true } = chunk || {};

                  let component = null;

                  if (type === "loader") {
                    component = (
                      <div className={classes.generatingMsg}>
                        <div className={loaderMessageClass}>
                          {isLocalised ? text : t(text)}
                        </div>
                        <div className={classes.dot} />
                      </div>
                    );
                  } else {
                    component = (
                      <MarkdownView
                        content={text}
                        contentContainerClass={blockContentClass}
                        components={markdownComponents}
                      />
                    );
                  }

                  return {
                    component,
                    type,
                    text,
                    xml,
                  };
                });
              } else {
                block = (
                  <MarkdownView
                    content={updatedContent}
                    contentContainerClass={blockContentClass}
                    components={markdownComponents}
                    // score based rubric is generated using HTML tables instead of Markdown
                    enableHTMLrendering={isStandardOrScoreRubric}
                  />
                );
              }

              const onClickAction = async params => {
                const { key, fieldType, localisationParams } = params || {};

                // check if previously user had selected "do not show this message again" checkbox
                // if yes then do not show the dialog
                const hasUserSeenReplaceDialog = getSettingValue({
                  name: "ShowReplaceContentDialog",
                  readFromUserAndOrgLevel: true,
                });

                const showDialog = params => {
                  const { action = "REPLACE_CONTENT" } = params || {};

                  setCurrentDialogueAction({
                    action,
                    params: {
                      key,
                      content,
                      label: _.get(localisationParams, "label", ""),
                      assessmentToolTypes,
                      language,
                      metadata,
                    },
                  });
                };

                if (key === "add_to_le") {
                  const someFieldsHasValue = _.some(
                    _.keys(assessmentFieldValues),
                    fieldType => {
                      return getSelectedFieldHasValue({
                        assessmentDetails,
                        selectedAssessmentField: fieldType,
                      });
                    }
                  );

                  if (assessmentFieldValues?.title) {
                    // reset title field error state (if any)
                    middlePaneRef?.Refs?.title?.resetErrorState?.();
                  }

                  if (someFieldsHasValue && !hasUserSeenReplaceDialog) {
                    showDialog();
                    return;
                  }
                } else {
                  if (fieldType) {
                    const fieldHasValue = getSelectedFieldHasValue({
                      assessmentDetails,
                      selectedAssessmentField: fieldType,
                    });

                    const isMYPRubric = getIsMYPRubric(fieldType);

                    const { isMismatch = false } = getMYPObjectivesData({
                      field: fieldType,
                      content,
                      assessmentDetails,
                      metadata,
                    });

                    if (
                      (fieldHasValue && !hasUserSeenReplaceDialog) ||
                      (isMYPRubric && isMismatch)
                    ) {
                      if (isMYPRubric) {
                        showDialog({ action: "REPLACE_CONTENT_MYP_RUBRIC" });
                      } else {
                        showDialog();
                      }
                      return;
                    }
                  }
                }

                handleActions({
                  key,
                  content,
                  assessmentToolTypes,
                  language,
                  metadata,
                });
              };

              const actionsList = getBlockActions({
                t,
                assessmentType,
                currentAssessmentTypes,
                assessmentToolTypes,
                assessmentFieldValues,
                isAddButtonDisabled,
                isAITutor,
              });

              const actions = (
                <div className={classes.blockActions}>
                  {_.map(actionsList, action => {
                    const {
                      key,
                      label,
                      icon,
                      disabled = false,
                      disabledTooltip,
                      isLocalised = false,
                      tooltipProps = {},
                      localisationParams,
                      fieldType,
                    } = action || {};

                    return (
                      <ConditionalWrapper
                        key={key}
                        type={"wrapperAsElement"}
                        condition={disabled && !_.isEmpty(disabledTooltip)}
                        wrapper={
                          <Tooltip
                            placement={"bottom"}
                            tooltip={
                              isLocalised
                                ? disabledTooltip
                                : localisationParams
                                ? t(disabledTooltip, localisationParams)
                                : t(disabledTooltip)
                            }
                            overlayInnerStyle={styles.overlayInnerStyle}
                            {...tooltipProps}
                            showArrow={true}
                          />
                        }
                      >
                        <Button
                          key={key}
                          size={"small"}
                          variant="neutral"
                          type="inline"
                          icon={icon}
                          onClick={() =>
                            onClickAction({
                              key,
                              fieldType,
                              localisationParams,
                            })
                          }
                          disabled={disabled}
                          testId={`${testId}-lessonPlanningAssistant-${key}`}
                        >
                          {localisationParams
                            ? t(label, localisationParams)
                            : t(label)}
                        </Button>
                      </ConditionalWrapper>
                    );
                  })}
                </div>
              );

              const isLastMessage = _.size(messages) - 1 === index;

              const showActions =
                !userInputFlagged &&
                _.get(metadata, "showActions", true) &&
                (!isLastMessage ||
                  (isLastMessage && !getIsAIResponseLoading(currentAIState)));

              const renderBlock = () => {
                if (_.isArray(block)) {
                  return _.map(block, (item, index) => {
                    const { component, type, xml } = item || {};
                    const id = `${node?.id}_${index}`;

                    const isTutorIdeasMessage = type === "tutor";
                    const isPlainTextWithoutAction =
                      type === "plaintext_without_actions";
                    const isLoader = type === "loader";

                    const onClickSelectButton = () => {
                      setSelectedTutorIdeaId(id);

                      // get the text content from the XML tags
                      const xmlToObj = convertXMLToObj(xml);

                      // set the text content as context (to be passed in the next AI response)
                      setContext(prevState => ({
                        ...prevState,
                        ...xmlToObj,
                      }));

                      // find draft AI prompt
                      const prompt = _.find(prompts, {
                        action: "draft_ai_tutor",
                      });

                      // set the prompt as action
                      setSelectedAction({
                        ...(prompt
                          ? prompt
                          : {
                              action: "draft_ai_tutor",
                              fieldType: "BRAINSTORM",
                              displayText: "Draft AI tutor", // not being used as we're not creating user message, but need to pass in input text to AI
                            }),
                        createUserMessage: false,
                        shouldFocusContentEditable: false,
                      });

                      setShouldScrollToBottom(true);
                    };

                    return (
                      <div key={id} className={blockWrapperClass}>
                        <div className={blockClass}>{component}</div>

                        {showActions &&
                        !isPlainTextWithoutAction &&
                        !isLoader ? (
                          <div className={classes.actionsContainer}>
                            {!isTutorIdeasMessage ? actions : null}

                            {/* just to show debug mode to dev user */}
                            <AIFeedbackStrip
                              showWarningText={false}
                              containerStyle={styles.containerStyle}
                              defaultFeedbackStatus={votes.myVote}
                              usageMetricsId={usageMetricsId}
                              model={model}
                              temperature={temperature}
                              intentUsageMetricsId={intentUsageMetricsId}
                              sourceFrom={"LESSON_PLANNING_ASSISTANT"}
                              showLikeButton={!isTutorIdeasMessage}
                              showDislikeButton={!isTutorIdeasMessage}
                            />

                            {isTutorIdeasMessage ? (
                              selectedTutorIdeaId === id ? (
                                <Button
                                  icon={
                                    <TickCircleFilled variant={"success"} />
                                  }
                                  size={"small"}
                                  type={"plain"}
                                  variant={"neutral"}
                                  onClick={onClickSelectButton}
                                  disabled={getIsAIResponseLoading(
                                    currentAIState
                                  )}
                                  dsVersion={"2.0"}
                                  testId={`${testId}-lessonPlanningAssistant-ideaSelected`}
                                >
                                  {t("common:capitalize", {
                                    text: t("common:selected"),
                                  })}
                                </Button>
                              ) : (
                                <Button
                                  icon={<TickSmallOutlined />}
                                  size={"small"}
                                  type={"plain"}
                                  variant={"neutral"}
                                  onClick={onClickSelectButton}
                                  disabled={getIsAIResponseLoading(
                                    currentAIState
                                  )}
                                  dsVersion={"2.0"}
                                  testId={`${testId}-lessonPlanningAssistant-ideaSelect`}
                                >
                                  {t("common:select")}
                                </Button>
                              )
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    );
                  });
                }

                return (
                  <div className={blockWrapperClass}>
                    <div className={blockClass}>{block}</div>
                    {showActions ? (
                      <div className={classes.actionsContainer}>
                        {actions}
                        <AIFeedbackStrip
                          showWarningText={false}
                          containerStyle={styles.containerStyle}
                          defaultFeedbackStatus={votes.myVote}
                          usageMetricsId={usageMetricsId}
                          model={model}
                          temperature={temperature}
                          intentUsageMetricsId={intentUsageMetricsId}
                          sourceFrom={"LESSON_PLANNING_ASSISTANT"}
                        />
                      </div>
                    ) : null}
                  </div>
                );
              };

              return !_.isEmpty(block) ? (
                <div key={id} className={classes.assistantMsg}>
                  <div className={classes.assistantMsgUpper}>
                    <Shifu3D
                      width={40}
                      height={40}
                      className={classes.assistantAvatar}
                    />
                  </div>

                  <div className={classes.flexDirectionColumn}>
                    {renderBlock()}
                  </div>
                </div>
              ) : null;
            }

            // "prerequisite" has been replaced by "AIContextBar" but it is still being used for "MYP objective rubric/MYP IDU rubric" criteria selection
            // Type "Create a MYP Rubric/IDU rubric" without selection criteria before to see the "prerequisite" message
            case "prerequisite": {
              const isLastMessage = _.size(messages) - 1 === index;
              const isLastMessagePrerequisite =
                isLastMessage && !getIsAIResponseLoading(currentAIState);

              if (!isLastMessagePrerequisite) return null;

              return (
                <div className={classes.assistantMsg}>
                  <PrerequisiteSelection
                    t={t}
                    prerequisiteFieldsData={metadata || {}}
                    portalType={portalType}
                    fieldList={fieldList}
                    stepList={stepList}
                    assessmentDetails={assessmentDetails}
                    context={context}
                    setContext={setContext}
                    mode={mode}
                    middlePaneRef={middlePaneRef}
                    isFromUnitPlan={isFromUnitPlan}
                    sourceFrom={sourceFrom}
                    setSelectedAction={setSelectedAction}
                    messages={messages}
                    courseId={courseId}
                    unitPlanTemplate={unitPlanTemplate}
                    unitPlanDetails={unitPlanDetails}
                    refetchAssessments={refetchAssessments}
                    course={course}
                    excludedFields={excludedFields}
                    setExcludedFields={setExcludedFields}
                    currentAIState={currentAIState}
                  />
                </div>
              );
            }
          }
        })}

        {showLoadingAnimation ? (
          <div className={classes.assistantMsg}>
            <div className={classes.generatingContent}>
              <Shifu3D
                width={40}
                height={40}
                className={classes.assistantAvatar}
              />
              <div className={classes.generatingMsgContainer}>
                <div className={classes.generatingMsg}>
                  <div className={classes.generatingSingleMsg}>
                    {renderLoadingText()}
                  </div>
                  <div className={classes.dot} />
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </React.Fragment>
    );
  };

  const renderHeader = () => {
    if (isInitialState) {
      const { label, localeLabel, pluralLocaleLabel } =
        currentAssessmentTypes?.[assessmentType] || {};

      const _label = pluralLocaleLabel || localeLabel;
      const assessmentTypeLabel = _label ? t(_label) : label;

      // classes
      const headerEmptyStateClass = classNames(classes.headerEmptyState, {
        [classes.headerEmptyStateAITutor]: isAITutor,
      });

      const titleClass = classNames("heading-5", classes.textDefault);

      const subTitleClass = classNames("text-body-2", classes.subtitle, {
        [classes.aiTutorSubtitle]: isAITutor,
      });

      const aiTutorTitleClass = classNames(classes.aiTutorEmptyStateTitle, {
        [classes.aiTutorEmptyStateTitleWithoutPrompts]: isPromptsHidden,
      });

      return (
        <header className={headerEmptyStateClass}>
          <div className={classes.titleContainer}>
            <Shifu3D
              width={64}
              height={64}
              className={classes.circleAssistantAvatar}
            />

            {isAITutor ? (
              <span className={aiTutorTitleClass}>
                {t(emptyStateLocales.aiTutorTitle)}
              </span>
            ) : (
              <span className={titleClass}>
                {t("classRoom:toddle_ai_empty_state_greet_text")}
              </span>
            )}
          </div>

          {!isAITutor ? (
            <div className={subTitleClass}>
              {t(emptyStateLocales.assessmentGreetMessage, {
                label: assessmentTypeLabel,
              })}
            </div>
          ) : null}

          {setShowAssistantRightPane ? (
            <IconButton
              dsVersion="2.0"
              size={"medium"}
              variant="neutral"
              type="plain"
              icon={<CloseOutlined variant="subtle" />}
              className={classes.closeButton}
              onClick={() => setShowAssistantRightPane(false)}
              testId={`${testId}-lessonPlanningAssistant-closeRightPane-initialState`}
            />
          ) : null}
        </header>
      );
    } else {
      if (isAITutor) return null;

      return (
        <header className={classes.header}>
          <div className={classes.flexWithGap}>
            <Shifu3D
              width={40}
              height={40}
              className={classes.assistantAvatar}
            />
            <div className={"text-label-2"}>
              {t("classRoom:toddle_ai_your_teaching_assistant")}
            </div>
          </div>
          <div className={classes.flexWithGap}>
            <Tooltip
              tooltip={t("common:clear_chat_history")}
              placement={"bottom"}
              showArrow={true}
            >
              <IconButton
                dsVersion="2.0"
                size={"medium"}
                variant="neutral"
                type="plain"
                disabled={
                  getIsAIResponseLoading(currentAIState) || _.isEmpty(messages)
                }
                icon={<RefreshArrowOutlined variant="neutral" />}
                onClick={() => {
                  setCurrentDialogueAction({
                    action: "CLEAR_CHAT",
                    params: {},
                  });
                }}
                testId={`${testId}-lessonPlanningAssistant-resetChat`}
              />
            </Tooltip>

            {setShowAssistantRightPane ? (
              <IconButton
                dsVersion="2.0"
                size={"medium"}
                variant="neutral"
                type="plain"
                icon={<CloseOutlined variant="neutral" />}
                onClick={() => setShowAssistantRightPane(false)}
                testId={`${testId}-lessonPlanningAssistant-closeRightPane`}
              />
            ) : null}
          </div>
        </header>
      );
    }
  };

  const renderBodyContainerEnd = () => {
    if (getIsAIResponseLoading(currentAIState)) {
      return <div className={classes.scrollEnd} ref={scrollEndRef} />;
    } else {
      const showDisclaimerText =
        !isInitialState &&
        _.some(messages, ({ node }) => node?.authorType === "assistant");

      if (showDisclaimerText) {
        const disclaimerTextClass = classNames({
          [classes.disclaimerText]: true,
          [classes.margin24]: _.last(messages)?.node?.authorType === "user",
        });

        return (
          <div className={disclaimerTextClass} ref={scrollEndRef}>
            {t("ai:ai_can_make_mistakes_msg")}
          </div>
        );
      }
    }

    return null;
  };

  const onPromptSuggestionModalClose = async params => {
    if (params) await onDialogConfirm(params);
    updatePromptSuggestionModalInfo({ showModal: false });
  };

  const footerTopComp =
    getIsAIResponseLoading(currentAIState) &&
    currentDialogAction?.action !== "CLEAR_CHAT" ? (
      <Button
        className={classes.stopGeneratingBtnRevamp}
        size={"medium"}
        variant={"outlined"}
        type={"outlined"}
        onClick={onClickStop}
        style={elevations["elevation-2B"]}
        dsVersion={"2.0"}
        testId={`${testId}-lessonPlanningAssistant-stopGenerating`}
      >
        {t("common:stop_with_label", {
          label: t("classRoom:generating"),
        })}
      </Button>
    ) : null;

  const onClickFooterOption = id => {
    switch (id) {
      case "suggestion_library": {
        updatePromptSuggestionModalInfo({
          showModal: true,
          params: {},
        });
      }
    }
  };

  const onClickDoNotShowAgain = () => {
    const { isChecked } = dialogParams || {};
    setCurrentDialogueAction({
      action: dialogAction,
      params: {
        ...dialogParams,
        isChecked: !isChecked,
      },
    });
  };

  // classes

  const bodyContainerClass = classNames({
    [classes.bodyContainer]: true,
    [classes.bodyContainerWithMessages]: !isInitialState,
    [classes.emptyStateBodyContainer]: isInitialState && !isPromptsHidden,
    [classes.emptyStateBodyContainerAITutor]: isInitialState && isAITutor,
  });

  const innerContainerClass = classNames({
    [classes.innerContainer]: true,
    [classes.innerContainerWithMessages]: !isInitialState,
    [classes.innerContainerWithoutPrompts]: isPromptsHidden,
  });

  const containerClass = classNames({
    [classes.container]: true,
    [classes.fullWidthContainer]: isAITutor,
    [classes.containerBorderLeft]: !isAITutor, // this component is rendered on the left side for Ai tutor hence border left not required
    [classes.containerBorderTop]: isAITutor && !isInitialState,
  });

  if ((!isData && isLoading) || currentAIState === LOADING) {
    return (
      <div className={containerClass} ref={containerRef}>
        <Loading />
      </div>
    );
  }

  return (
    <div className={containerClass} ref={containerRef}>
      {showLoader ? (
        <Loading />
      ) : (
        <div className={innerContainerClass}>
          {renderHeader()}
          <div className={bodyContainerClass} onScroll={onScrollBodyContainer}>
            {!_.isEmpty(messages) ? renderMessages() : renderEmptyState()}
            {renderBodyContainerEnd()}
          </div>
        </div>
      )}

      <AIContextBar
        selectedAction={selectedAction}
        setSelectedAction={setSelectedAction}
        isInitialState={isInitialState}
        onSendMessage={onSendMessage}
        footerTopComp={footerTopComp}
        context={context}
        setContext={setContext}
        excludedFields={excludedFields}
        setExcludedFields={setExcludedFields}
        creationMode={creationMode}
        sourceFrom={sourceFrom}
        mode={mode}
        isFromUnitPlan={isFromUnitPlan}
        middlePaneRef={middlePaneRef}
        idDraft={isDraft}
        portalType={portalType}
        onClickFooterOption={onClickFooterOption}
        isWritingMessage={getIsAIResponseLoading(currentAIState)}
        setConfigureAIVoiceRef={setConfigureAIVoiceRef}
        courseId={courseId}
        selectedCourse={selectedCourse}
        leId={leId}
        conversationMetadata={conversationMetadata}
        conversationAttachments={conversationAttachments}
        prompts={prompts}
        isAITutor={isAITutor}
        aiConversationId={aiConversationId}
        templateId={templateId}
        testId={testId}
        initialValue={initialValue}
        learningCourseId={learningCourseId}
        unitPlanTemplate={
          isLearningCoursesEnabled ? unitPlanTemplate : undefined
        }
        unitPlanDetails={isLearningCoursesEnabled ? unitPlanDetails : undefined}
        isSpeechEnabled={true}
      />

      <Modal
        isOpen={!_.isEmpty(dialogAction)}
        onClose={() => setCurrentDialogueAction({ action: "" })}
        width={DIALOGS_INFO?.[dialogAction]?.width || "440px"}
        height={"fit-content"}
        zIndex={1111}
        dsVersion={"2.0"}
      >
        <Modal.Header
          title={DIALOGS_INFO?.[dialogAction]?.title({
            t,
            ...dialogParams,
          })}
          isClosable={true}
        />

        <Modal.Body>
          <div className={classes.modalBody}>
            {DIALOGS_INFO?.[dialogAction]?.message({
              t,
              ...dialogParams,
            })}

            {DIALOGS_INFO?.[dialogAction]?.showDoNotShowThisAgain ? (
              <div
                role={"button"}
                tabIndex={0}
                className={classes.modalCheckboxContainer}
                onClick={onClickDoNotShowAgain}
                onKeyDown={e => onEnterOrSpaceKeyDown(e, onClickDoNotShowAgain)}
              >
                <Checkbox
                  isChecked={dialogParams?.isChecked}
                  testId={`${testId}-lessonPlanningAssistant-modalDoNotShowAgain`}
                />
                <span>{t("common:dont_show_this_message_again")}</span>
              </div>
            ) : null}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <footer className={classes.modalFooter}>
            <Button
              dsVersion={"2.0"}
              variant={"neutral"}
              type={"outlined"}
              size={"medium"}
              onClick={() => setCurrentDialogueAction({ action: "" })}
              testId={`${testId}-lessonPlanningAssistant-modalCancel`}
            >
              {DIALOGS_INFO?.[dialogAction]?.buttonOneText({
                t,
                ...dialogParams,
              })}
            </Button>

            <Button
              dsVersion={"2.0"}
              variant={"destructive"}
              type={"fill"}
              size={"medium"}
              onClick={() => onDialogConfirm()}
              testId={`${testId}-lessonPlanningAssistant-modalConfirm`}
            >
              {DIALOGS_INFO?.[dialogAction]?.buttonTwoText({
                t,
                ...dialogParams,
              })}
            </Button>
          </footer>
        </Modal.Footer>
      </Modal>

      {promptSuggestionModalInfo?.showModal ? (
        <SuggestionLibrary
          t={t}
          initialGroupType={_.get(promptSuggestionModalInfo, "params.id")}
          onPromptSuggestionModalClose={onPromptSuggestionModalClose}
          onPromptSelection={onPromptSelection}
          prompts={prompts}
          language={aiVoiceModalLanguage}
          isAIResponseLoading={getIsAIResponseLoading(currentAIState)}
          assessmentDetails={assessmentDetails}
          unitPlanDetails={unitPlanDetails}
          conversationMetadata={conversationMetadata}
          setCurrentDialogueAction={setCurrentDialogueAction}
          isInitialState={isInitialState}
          isAITutor={isAITutor}
          selectedAction={selectedAction}
          setSelectedAction={setSelectedAction}
          leId={leId}
          gradeId={gradeId}
          course={course}
          assessmentTemplate={assessmentTemplate}
          portalType={portalType}
          entityType={entityType}
          learningCourseId={learningCourseId}
        />
      ) : null}
    </div>
  );
});

LessonPlanningAssistant.displayName = "LessonPlanningAssistant";

LessonPlanningAssistant.defaultProps = {
  portalType: "PLANNER",
  testId: "",
};

const mapStateToProps = state => {
  const plannerElementSets = _.get(
    state,
    "platform.currentPlannerElementSets",
    []
  );
  const academicYearId = getSelectedAcademicYearId(state);
  const curriculumProgramId = getSelectedCurriculumId(state);
  const curriculumType = getCurrentCurriculumProgramType(state);
  const userId = getUserId(state);
  const userType = getUserType(state);
  const currentSelectedCourse = _.get(
    state,
    "teacher.selected_class.selected_course"
  );

  // when coming from unit flow
  // set AI starting chat text to description of assessment
  const shouldSetInitialPrompt = _.get(
    state,
    "unitFlow.openAssessmentAIPaneOnMount",
    false
  );

  return {
    userId,
    userType,
    plannerElementSets,
    curriculumType,
    currentSelectedCourse,
    academicYearId,
    curriculumProgramId,
    shouldSetInitialPrompt,
    organizationId: getOrganizationId(state),
  };
};

const getDerivedProps = ownProps => {
  const { isAITutor, currentSelectedCourse, academicYearId } = ownProps;

  const courseId =
    _.get(ownProps, "courseId") ||
    _.get(ownProps, "selectedCourse.courseId") ||
    currentSelectedCourse;

  const courseGradeFilters = {
    academicYearId,
  };

  const collaboratorsFilter = {
    userAcademicYearId: academicYearId,
  };

  const isAITutorEnhancementsV2Enabled = getIsAITutorEnhancementsV2Enabled();

  return {
    containerStyle: {
      minWidth: isAITutor ? "480px" : "610px", // below 480px, AIContextBar overflows
      backgroundColor: colors.surfaceWhite,
      borderLeft: `1px solid ${colors.borderSubtle}`,
    },
    courseId,
    isData: true,
    isLoading: false,
    showLoaderBar: false,
    courseGradeFilters,
    collaboratorsFilter,
    isAITutorEnhancementsV2Enabled,
  };
};

const mapActionCreators = {
  showIntercom,
  hideIntercom,
  setToastMsg,
  updateAssignmentField,
  resetAIConversation,
  onSendQuery,
  updateSettings,
  getCurrentAssessmentTypes,
  createAIConversation,
  updateAIConversation,
  captureAndUploadImage,
  createAssessmentTool,
  removeAssessmentTool,
};

export default compose(
  I18nHOC({ resource: ["ai", "common"] }),
  connect(mapStateToProps, mapActionCreators),
  withDerivedProps(getDerivedProps),
  // fetches assessment details data for context
  getAssessmentDetailsForAIEnhancer,
  // fetches unit plan data from context state when learning course is enabled
  graphql(getUnitPlanBasicDetailsQuery, {
    name: "getUnitPlanBasicDetails",
    skip: ({ context }) =>
      !getIsLearningCoursesEnabled() || _.isEmpty(context?.unitPlan?.id),
    options: ({ context, portalType }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: context?.unitPlan?.id, portalType },
    }),
    props({
      getUnitPlanBasicDetails,
      ownProps: {
        t,
        i18n,
        curriculumType,
        assessmentTemplate,
        isData,
        isLoading,
      },
    }) {
      const { node = {}, networkStatus } = getUnitPlanBasicDetails;

      const unitPlanTemplateId = _.get(node, "templateId", null);

      const _unitPlanDetails = {
        unitPlanId: _.get(node, "id"),
        grades: _.get(node, "grades", []),
        unitPlanFields: _.get(node, "allFields", []),
        unitPlanResolvedMinimalTree: node || {},
      };

      const _unitPlanTemplate = getPlannerTemplateFromCache(
        getVariablesForLocalisedPlannerTemplates({
          templates: [unitPlanTemplateId],
          language: i18n.language,
        })
      );

      const resolvedSubjectNodes = _.get(
        _.find(_unitPlanDetails.unitPlanFields, { uid: "subjects" }),
        "resolvedMinimalTree.subjects",
        []
      );

      // modified unit plan template
      const unitPlanTemplate = _.get(
        changeDisciplinaryContentToMultiField({
          t,
          template: _unitPlanTemplate,
          unitType: getFieldValue({
            fields: _unitPlanDetails?.unitPlanFields,
            uid: "unitType",
          }),
          curriculumType,
          resolvedSubjectNodes,
        }),
        "body",
        {}
      );

      // modified unit plan details
      const unitPlanDetails = getUnitPlanDetails({
        unitPlanDetails: _unitPlanDetails,
        assessmentTemplate,
      });

      return {
        unitPlanDetails,
        unitPlanTemplate,
        isData: !_.isEmpty(node) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),

  // fetches conversation messages
  graphql(getAIConversationsQuery, {
    name: "getAIConversations",
    skip: ({ aiConversationId }) => _.isEmpty(aiConversationId),
    options: ({ aiConversationId, portalType }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: aiConversationId, portalType },
      context: {
        useHttpLink: true,
      },
    }),
    props({ getAIConversations, ownProps: { isData, isLoading } }) {
      const { node, networkStatus } = getAIConversations || {};

      const messages = [..._.get(node, "messages.edges", [])].reverse();

      const conversationMetadata = _.get(node, "metadata");
      const conversationAttachments = _.get(node, "attachments", []);

      return {
        messages,
        conversations: node,
        isInitialState: _.isEmpty(messages),
        conversationMetadata,
        conversationAttachments,
        isData: !_.isEmpty(node) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  // fetches prompts
  graphql(getAIPromptsQuery, {
    name: "getAIPrompts",
    skip: ({ leId, entityType }) => !leId || !entityType,
    options: ({
      leId,
      entityType,
      curriculumProgramId,
      portalType,
      gradeId,
      assessmentDetails,
      unitPlanDetails,
      curriculumType,
      isAITutor,
    }) => {
      const promptFilters = getAIPromptFilters({
        assessmentDetails,
        unitPlanDetails,
        curriculumProgramType: curriculumType,
        isAITutor,
      });

      return {
        fetchPolicy: "cache-and-network",
        variables: {
          filters: {
            entityId: leId,
            entityType,
            curriculumProgramId,
            gradeId,
            ...promptFilters,
          },
          portalType,
        },
      };
    },
    props({ getAIPrompts }) {
      const queryData = _.get(getAIPrompts, "ai", {});

      const prompts = _.get(queryData, "prompts", []);
      const networkStatus = _.get(getAIPrompts, "networkStatus");

      const showLoader =
        _.isEmpty(queryData) && _.includes([1, 2], networkStatus);

      return {
        prompts,
        showLoader,
        getAIPrompts,
      };
    },
  }),
  graphql(getSchoolisationInstructionsQuery, {
    name: "getSchoolisationInstructions",
    skip: () => !isSchoolisationEnabled(),
    options: () => ({
      fetchPolicy: "cache-and-network",
      variables: {
        module: "assignment_assistant_ai",
      },
    }),
    props({ getSchoolisationInstructions }) {
      const instructionsData = _.get(
        getSchoolisationInstructions,
        "ai.schoolisationInstructions",
        []
      );

      const schoolInstructionBlockForLE = _.map(instructionsData, block =>
        _.get(block, "content", "")
      ).join("\n\n");

      return {
        schoolInstructions: schoolInstructionBlockForLE || "",
      };
    },
  })
)(LessonPlanningAssistant);
